/**
 * Floating layers (design v2, D-104): dialog, menu, tooltip, toasts and the ⌘K command palette.
 * Thick glass for what floats above the page, keyboard first, Escape always closes, focus returns
 * to where it was. No colour here: classes from ui.css only.
 */

import {
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactElement,
  type ReactNode,
  type RefObject,
  cloneElement,
  createContext,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Modal behaviour: Escape closes, Tab stays inside, focus comes back to the opener. */
export function useModal(open: boolean, onClose: () => void, ref: RefObject<HTMLElement | null>): void {
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const node = ref.current;
    const first = node?.querySelector<HTMLElement>("[data-autofocus]") ?? node;
    first?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        close.current();
        return;
      }
      if (event.key !== "Tab" || !node) return;
      const items = [...node.querySelectorAll<HTMLElement>(FOCUSABLE)];
      const head = items[0];
      const tail = items[items.length - 1];
      if (!head || !tail) return;
      if (event.shiftKey && document.activeElement === head) {
        event.preventDefault();
        tail.focus();
      } else if (!event.shiftKey && document.activeElement === tail) {
        event.preventDefault();
        head.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      previous?.focus();
    };
  }, [open, ref]);
}

// ---------------------------------------------------------------- Dialog

export interface DialogProps {
  open: boolean;
  title: string;
  onClose: () => void;
  /** One or two sentences. */
  children?: ReactNode;
  /** Buttons, the confirming one last (right). */
  actions?: ReactNode;
  /** "alertdialog" for destructive confirmations. */
  role?: "dialog" | "alertdialog";
}

/** Small centred dialog (confirmation, short form). Use GlassSheet for longer content. */
export function Dialog({ open, title, onClose, children, actions, role = "dialog" }: DialogProps) {
  const titleId = useId();
  const bodyId = useId();
  const ref = useRef<HTMLDivElement>(null);
  useModal(open, onClose, ref);
  if (!open) return null;
  return (
    <>
      <div className="ao-scrim" aria-hidden="true" onClick={onClose} />
      <div
        ref={ref}
        role={role}
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={children ? bodyId : undefined}
        tabIndex={-1}
        className="ao-glass ao-glass--thick ao-dialog"
      >
        <h2 id={titleId} className="ao-dialog__title">
          {title}
        </h2>
        {children ? (
          <div id={bodyId} className="ao-dialog__body">
            {children}
          </div>
        ) : null}
        {actions ? <div className="ao-dialog__actions">{actions}</div> : null}
      </div>
    </>
  );
}

// ---------------------------------------------------------------- Menu

export type MenuEntry =
  | {
      label: string;
      onSelect: () => void;
      icon?: ReactNode;
      shortcut?: string;
      danger?: boolean;
      disabled?: boolean;
    }
  | { separator: true }
  | { heading: string };

export interface MenuProps {
  /** Accessible name of the trigger button. */
  label: string;
  /** Visible content of the trigger (icon, avatar, text). */
  trigger: ReactNode;
  items: MenuEntry[];
  align?: "start" | "end";
  /** Opens upwards (menus at the bottom of the sidebar). */
  placement?: "down" | "up";
  triggerClassName?: string;
}

/** Drop-down menu: arrow keys, Home/End, Escape, click outside. */
export function Menu({ label, trigger, items, align = "start", placement = "down", triggerClassName }: MenuProps) {
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const root = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);

  const focusItem = (index: number) => {
    const nodes = [...(root.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not([disabled])') ?? [])];
    if (!nodes.length) return;
    nodes[(index + nodes.length) % nodes.length]?.focus();
  };

  useEffect(() => {
    if (!open) return undefined;
    focusItem(0);
    const onDown = (event: PointerEvent) => {
      if (root.current && event.target instanceof Node && !root.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [open]);

  const onKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    const nodes = [...(root.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not([disabled])') ?? [])];
    const index = nodes.findIndex((node) => node === document.activeElement);
    if (event.key === "ArrowDown") focusItem(index + 1);
    else if (event.key === "ArrowUp") focusItem(index - 1);
    else if (event.key === "Home") focusItem(0);
    else if (event.key === "End") focusItem(nodes.length - 1);
    else if (event.key === "Escape" || event.key === "Tab") {
      setOpen(false);
      if (event.key === "Escape") button.current?.focus();
      return;
    } else return;
    event.preventDefault();
  };

  const position = [
    "ao-glass ao-glass--thick ao-menu",
    align === "end" ? "ao-menu--end" : "ao-menu--start",
    placement === "up" && "ao-menu--up",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="ao-menu-anchor" ref={root}>
      <button
        ref={button}
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        className={triggerClassName ?? "ao-button ao-button--plain ao-button--icon"}
        onClick={() => setOpen((value) => !value)}
      >
        {trigger}
      </button>
      {open ? (
        <div id={menuId} role="menu" aria-label={label} tabIndex={-1} className={position} onKeyDown={onKeyDown}>
          {items.map((item, index) => {
            if ("separator" in item) return <div key={index} role="separator" className="ao-menu__separator" />;
            if ("heading" in item)
              return (
                <div key={index} role="presentation" className="ao-menu__label">
                  {item.heading}
                </div>
              );
            return (
              <button
                key={index}
                type="button"
                role="menuitem"
                disabled={item.disabled}
                className={item.danger ? "ao-menu__item ao-menu__item--danger" : "ao-menu__item"}
                onClick={() => {
                  setOpen(false);
                  button.current?.focus();
                  item.onSelect();
                }}
              >
                {item.icon}
                <span>{item.label}</span>
                {item.shortcut ? <span className="ao-menu__shortcut">{item.shortcut}</span> : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------- Tooltip

export interface TooltipProps {
  content: ReactNode;
  /** One focusable element (button, link). */
  children: ReactElement<{ "aria-describedby"?: string }>;
  placement?: "above" | "below";
}

/** Short hint on hover and keyboard focus (never the only way to get an information). */
export function Tooltip({ content, children, placement = "above" }: TooltipProps) {
  const id = useId();
  const child = isValidElement(children) ? cloneElement(children, { "aria-describedby": id }) : children;
  return (
    <span className="ao-tooltip-anchor">
      {child}
      <span id={id} role="tooltip" className={placement === "below" ? "ao-tooltip ao-tooltip--below" : "ao-tooltip"}>
        {content}
      </span>
    </span>
  );
}

// ---------------------------------------------------------------- Toasts

export type ToastTone = "default" | "success" | "warning" | "danger";

interface ToastItem {
  id: number;
  message: ReactNode;
  tone: ToastTone;
  icon?: ReactNode;
  action?: { label: string; onClick: () => void };
}

export interface ToastOptions {
  tone?: ToastTone;
  icon?: ReactNode;
  /** ms, 4 000 by default; 0 keeps it until dismissed. */
  duration?: number;
  action?: { label: string; onClick: () => void };
}

interface ToastApi {
  show: (message: ReactNode, options?: ToastOptions) => number;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

/** Put once near the root. Toasts are announced politely to screen readers. */
export function ToastProvider({ children, dismissLabel }: { children: ReactNode; dismissLabel: string }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const next = useRef(1);
  const dismiss = useCallback((id: number) => setItems((list) => list.filter((item) => item.id !== id)), []);
  const show = useCallback(
    (message: ReactNode, { tone = "default", icon, duration = 4000, action }: ToastOptions = {}) => {
      const id = next.current;
      next.current += 1;
      setItems((list) => [...list.slice(-2), { id, message, tone, icon, action }]);
      if (duration > 0) window.setTimeout(() => dismiss(id), duration);
      return id;
    },
    [dismiss],
  );
  const api = useMemo(() => ({ show, dismiss }), [show, dismiss]);
  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="ao-toast-region" role="status" aria-live="polite">
        {items.map((item) => (
          <div key={item.id} className={item.tone === "default" ? "ao-toast" : `ao-toast ao-toast--${item.tone}`}>
            {item.icon ? (
              <span className="ao-toast__icon" aria-hidden="true">
                {item.icon}
              </span>
            ) : null}
            <span className="ao-toast__message">{item.message}</span>
            {item.action ? (
              <button
                type="button"
                className="ao-button ao-button--ghost ao-button--sm"
                onClick={() => {
                  item.action?.onClick();
                  dismiss(item.id);
                }}
              >
                {item.action.label}
              </button>
            ) : null}
            <button
              type="button"
              className="ao-button ao-button--plain ao-button--sm ao-button--icon"
              aria-label={dismissLabel}
              onClick={() => dismiss(item.id)}
            >
              <span aria-hidden="true">×</span>
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (!api) throw new Error("useToast must be used inside <ToastProvider>");
  return api;
}

// ---------------------------------------------------------------- Command palette (⌘K)

export interface CommandItem {
  id: string;
  label: string;
  group?: string;
  hint?: string;
  icon?: ReactNode;
  /** Extra words that also match (synonyms, other language). */
  keywords?: string;
  onSelect: () => void;
}

export interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  items: CommandItem[];
  /** Accessible name of the palette. */
  label: string;
  placeholder: string;
  emptyText: string;
  /** Footer hints, already translated: e.g. { navigate: "Naviguer", open: "Ouvrir", close: "Fermer" }. */
  hints?: { navigate: string; open: string; close: string };
  searchIcon?: ReactNode;
}

const normalize = (text: string): string =>
  text
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

/** Opens with ⌘K / Ctrl+K anywhere (and with `/` outside fields). */
export function useCommandShortcut(onOpen: () => void): void {
  const handler = useRef(onOpen);
  handler.current = onOpen;
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const typing =
        event.target instanceof HTMLElement &&
        (event.target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(event.target.tagName));
      if ((event.key === "k" || event.key === "K") && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        handler.current();
      } else if (event.key === "/" && !typing) {
        event.preventDefault();
        handler.current();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);
}

export function CommandPalette({
  open,
  onClose,
  items,
  label,
  placeholder,
  emptyText,
  hints,
  searchIcon,
}: CommandPaletteProps) {
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const listId = useId();
  useModal(open, onClose, ref);
  useEffect(() => {
    if (open) {
      setQuery("");
      setActive(0);
    }
  }, [open]);

  const results = useMemo(() => {
    const needle = normalize(query.trim());
    if (!needle) return items;
    return items.filter((item) => normalize(`${item.label} ${item.group ?? ""} ${item.hint ?? ""} ${item.keywords ?? ""}`).includes(needle));
  }, [items, query]);

  useEffect(() => {
    const option = document.getElementById(`${listId}-${active}`);
    if (typeof option?.scrollIntoView === "function") option.scrollIntoView({ block: "nearest" });
  }, [active, listId]);

  if (!open) return null;

  const choose = (item: CommandItem | undefined) => {
    if (!item) return;
    onClose();
    item.onSelect();
  };
  const onKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") setActive((i) => Math.min(results.length - 1, i + 1));
    else if (event.key === "ArrowUp") setActive((i) => Math.max(0, i - 1));
    else if (event.key === "Enter") choose(results[active]);
    else return;
    event.preventDefault();
  };

  let lastGroup: string | undefined;
  return (
    <>
      <div className="ao-scrim" aria-hidden="true" onClick={onClose} />
      <div ref={ref} role="dialog" aria-modal="true" aria-label={label} className="ao-glass ao-glass--thick ao-command">
        <div className="ao-command__search">
          {searchIcon}
          <input
            data-autofocus
            className="ao-command__input"
            role="combobox"
            aria-expanded="true"
            aria-controls={listId}
            aria-activedescendant={results.length ? `${listId}-${active}` : undefined}
            aria-autocomplete="list"
            aria-label={label}
            placeholder={placeholder}
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setActive(0);
            }}
            onKeyDown={onKeyDown}
          />
          <kbd className="ao-kbd">esc</kbd>
        </div>
        {results.length ? (
          <ul id={listId} role="listbox" aria-label={label} className="ao-command__list">
            {results.map((item, index) => {
              const heading = item.group && item.group !== lastGroup ? item.group : null;
              lastGroup = item.group;
              return (
                <li key={item.id} role="presentation">
                  {heading ? (
                    <div className="ao-command__group" role="presentation">
                      {heading}
                    </div>
                  ) : null}
                  <div
                    id={`${listId}-${index}`}
                    role="option"
                    aria-selected={index === active}
                    className="ao-command__item"
                    tabIndex={-1}
                    onPointerMove={() => setActive(index)}
                    onClick={() => choose(item)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") choose(item);
                    }}
                  >
                    {item.icon}
                    <span>{item.label}</span>
                    {item.hint ? <span className="ao-command__hint">{item.hint}</span> : null}
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="ao-command__empty">{emptyText}</p>
        )}
        {hints ? (
          <div className="ao-command__footer" aria-hidden="true">
            <span>
              <kbd className="ao-kbd">↑</kbd>
              <kbd className="ao-kbd">↓</kbd>
              {hints.navigate}
            </span>
            <span>
              <kbd className="ao-kbd">↵</kbd>
              {hints.open}
            </span>
            <span>
              <kbd className="ao-kbd">esc</kbd>
              {hints.close}
            </span>
          </div>
        ) : null}
      </div>
    </>
  );
}
